import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api } from '../services/api.js';
import { TEMPORARY_UNLISTED_CATEGORY, statusLabel } from '../utils/statusMeta.js';
import { fmtMoney, fmtDate } from '../utils/format.js';
import QRCode from 'react-qr-code';

export default function ItemDetail() {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Inline bounty editor
  const [editingBounty, setEditingBounty] = useState(false);
  const [bountyInput, setBountyInput] = useState('');

  function reload() {
    return api.getItem(itemId)
      .then((it) => setItem(it))
      .catch((e) => setErr(e.message));
  }
  useEffect(() => { reload(); }, [itemId]);

  async function markRecovered() {
    setBusy(true);
    try { await api.recoverItem(itemId); await reload(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function markLost() {
    setBusy(true);
    try { await api.markItemLost(itemId); await reload(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function markActive() {
    // LOST → ACTIVE ("found it myself"), and legacy RECOVERED rows → ACTIVE.
    setBusy(true);
    try { await api.reactivateItem(itemId); await reload(); }
    catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function deleteItem() {
    setBusy(true);
    try {
      await api.deleteItem(itemId);
      navigate('/dashboard');
    } catch (e) { setErr(e.message); setBusy(false); setConfirmDelete(false); }
  }

  function startBountyEdit() {
    setBountyInput(((item.bountyCents ?? 0) / 100).toString());
    setEditingBounty(true);
  }

  async function saveBounty() {
    const cents = Math.round(parseFloat(bountyInput || '0') * 100);
    if (Number.isNaN(cents) || cents < 0) { setErr('Enter a valid amount.'); return; }
    setBusy(true);
    try {
      await api.updateItem(itemId, { bountyCents: cents });
      await reload();
      setEditingBounty(false);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  if (err && !item) return <div className="alert error">{err}</div>;
  if (!item) return <div className="muted center loading-block">Loading…</div>;

  const photos = item.photos ?? [];
  const shortUrl = import.meta.env.VITE_SHORT_URL ?? 'https://e.ezbounty.com';
  const claimUrl = `${shortUrl}/I/${item.itemId}`;
  const recoveredCount = item.recoveredCount ?? item.recoveryHistory?.length ?? 0;

  // Top-bar lifecycle button: ACTIVE → "Mark as lost"; LOST → "Found it";
  // legacy RECOVERED rows → "Mark as active". Hidden mid-dance.
  const lifecycleButton =
    item.status === 'ACTIVE' ? (
      <button className="btn btn-ghost btn-sm"
        disabled={busy} onClick={markLost}>
        Mark as lost
      </button>
    ) : item.status === 'LOST' ? (
      <button className="btn btn-ghost btn-sm"
        disabled={busy} onClick={markActive}>
        Found it — mark active
      </button>
    ) : item.status === 'RECOVERED' ? (
      <button className="btn btn-ghost btn-sm"
        disabled={busy} onClick={markActive}>
        Mark as active
      </button>
    ) : null;

  return (
    <div className="container-narrow page">
      <div className="row between">
        <Link to="/dashboard" className="muted tiny">← Inventory</Link>
        <div className="row gap-8">
          {lifecycleButton}
          <Link to={`/items/${itemId}/edit`} className="btn btn-ghost btn-sm">
            Edit
          </Link>
          {!confirmDelete ? (
            <button className="btn btn-danger btn-sm"
              disabled={busy} onClick={() => setConfirmDelete(true)}>
              Delete
            </button>
          ) : (
            <>
              <span className="muted tiny">Delete permanently?</span>
              <button className="btn btn-danger btn-sm"
                disabled={busy} onClick={deleteItem}>
                {busy ? '…' : 'Yes, delete'}
              </button>
              <button className="btn btn-ghost btn-sm"
                disabled={busy} onClick={() => setConfirmDelete(false)}>
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {err && <div className="alert error mt-12">{err}</div>}

      <h2 className="mt-20">{item.name}</h2>
      <div className="row gap-10 mt-8 mb-24">
        <span className={`status status-${item.status}`}>{statusLabel(item.status)}</span>
        {recoveredCount > 0 && (
          <span title="Times this item has been recovered" className="pill tone-done">
            Recovered {recoveredCount}×
          </span>
        )}
      </div>

      {photos.length > 0 && (
        <div className="gallery">
          {photos.map((p, i) => (
            <a
              key={p.key}
              href={p.urls.original}
              target="_blank"
              rel="noreferrer"
              aria-label={`View photo ${i + 1} full size`}
            >
              <img
                src={p.urls.display}
                alt=""
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
              />
            </a>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div>
            <div className="eyebrow">Bounty</div>
            <div className="row">
              {!editingBounty ? (
                <>
                  <div className="bounty-amount">
                    {fmtMoney(item.bountyCents, item.currency)}
                  </div>
                  <button className="btn btn-ghost btn-xs"
                    disabled={busy} onClick={startBountyEdit}>
                    Change bounty
                  </button>
                </>
              ) : (
                <>
                  <span className="bounty-symbol" aria-hidden="true">$</span>
                  <input
                    type="number" min={0} step="1" value={bountyInput}
                    onChange={(e) => setBountyInput(e.target.value)}
                    className="bounty-input"
                    aria-label="New bounty amount"
                    autoFocus
                  />
                  <button className="btn btn-primary btn-sm"
                    disabled={busy} onClick={saveBounty}>
                    {busy ? '…' : 'Save'}
                  </button>
                  <button className="btn btn-ghost btn-sm"
                    disabled={busy} onClick={() => setEditingBounty(false)}>
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        <dl className="kv kv-wide mt-8">
          {item.description && (<>
            <dt className="muted tiny">Description</dt>
            <dd>{item.description}</dd>
          </>)}
          <dt className="muted tiny">Category</dt>
          <dd>
            {[item.category, item.subcategory].filter(Boolean).join(' › ') || 'Uncategorized'}
          </dd>
          {item.tags?.length > 0 && (<>
            <dt className="muted tiny">Tags</dt>
            <dd>{item.tags.join(', ')}</dd>
          </>)}
          {item.identifyingMarks && (<>
            <dt className="muted tiny">Marks (private)</dt>
            <dd className="muted">{item.identifyingMarks}</dd>
          </>)}
          <dt className="muted tiny">Added</dt>
          <dd>{fmtDate(item.createdAt)}</dd>
        </dl>

        {item.category === TEMPORARY_UNLISTED_CATEGORY && (
          <p className="muted tiny mt-14">
            This was created on the spot from a found report. Edit it and add a
            photo to make it a permanent item.
          </p>
        )}
      </div>

      {item.status === 'IN_RECOVERY' && (
        <div className="card mt-24">
          <span className="eyebrow">Recovery</span>
          <h3 className="italic mt-6 mb-12">
            You confirmed this is yours.
          </h3>
          <p className="muted mb-16">
            Once you physically have it back, mark it recovered to close this out.
          </p>
          <button className="btn btn-primary" disabled={busy} onClick={markRecovered}>
            {busy ? 'Saving…' : 'Mark as recovered'}
          </button>
        </div>
      )}

      <div className="card mt-24">
        <span className="eyebrow">If found</span>
        <h3 className="italic mt-6 mb-16">Share this with a finder.</h3>
        <p className="muted mb-18">
          Print this QR onto a small sticker and place it inside the item, or share the link directly.
          Anyone who finds it can use it to notify you — no account needed.
        </p>
        <div className="share-row">
          <div className="qr-frame">
            <QRCode value={claimUrl} size={164} level="M" title="QR code for this item" />
          </div>
          <div className="share-main">
            <div className="share-box">{claimUrl}</div>
            <button
              className="btn btn-ghost mt-12"
              onClick={() => {
                navigator.clipboard.writeText(claimUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}>
              {copied ? 'Copied!' : 'Copy link'}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-24">
        <Link to={`/items/${itemId}/claims`} className="btn btn-accent btn-block">
          View claims for this item
        </Link>
        <Link to={`/items/${item.itemId}/print`} className="btn btn-ghost">
          Print QR labels
        </Link>
      </div>
    </div>
  );
}
